import { useState } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Link,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProtectedImage } from "../ProtectedImage";
import {
  ListItem,
  postAddComicToLists,
  postAddReleaseToLists,
  postRemoveComicFromList,
  postRemoveReleaseFromList,
} from "../../api/lists";
import { SealCheckIcon } from "../icons/SealCheckIcon";
import { TrashcanIcon } from "../icons/reader/TrashcanIcon";
import { ConfirmAlertDialog } from "../common/ConfirmAlertDialog";
import { ReleaseCardMenu } from "../cards/ReleaseCardMenu";
import { READING_LISTS_SHELF_QUERY_KEY } from "../../screens/shelf/hooks/useReadingListsShelf";
import { readingListDetailQueryKey } from "../../screens/shelf/ReadingListDetailScreen";

// Per Figma node 3081:57247 ("Details release card large"). Rows are 102px
// tall, contained in their own subtle bordered card, with a 16px gap to the
// next row. The row exposes a left-aligned cover/numeral, a centered title
// block with progress rail, and a right cluster (status pill + kebab menu).
const ROW_HEIGHT = 102;
// Comic covers ship at a 65:100 aspect; match that so the underlying art
// reads cleanly through the numeral overlay instead of getting cropped to
// fit a square-ish slot.
const COVER_W = 60;
const COVER_H = 86;

export interface ReadingListDetailItemRowProps {
  item: ListItem;
  /** When true, owner-only kebab actions ("Remove from list") render. */
  isOwner: boolean;
  /** Parent list id — needed for the remove + undo flow. */
  listId: number;
  /** Whether the list holds releases (vs comics) — drives which API to call. */
  isReleaseList: boolean;
  /**
   * When provided, the row renders this node as a drag handle on the left,
   * AND skips the outer reader-link wrap so dragging doesn't fire navigation.
   * Caller is responsible for wiring dnd-kit listeners onto the handle.
   */
  dragHandle?: React.ReactNode;
}

function getContext(item: ListItem) {
  return item.comic ?? item.release ?? null;
}

function getReadHref(item: ListItem): string | null {
  const ctx = getContext(item);
  if (!ctx) return null;
  // Reader-first per product call: row click should land in the reading view.
  // Falls back to the comic/release detail page, then to a slug-style route.
  return ctx.read_url ?? ctx.url ?? (ctx.slug ? `/${ctx.slug}` : null);
}

function getDetailsHref(item: ListItem): string | null {
  const ctx = getContext(item);
  if (!ctx) return null;
  // Detail-first href used by the "Release details" menu item — never the
  // reader URL.
  return ctx.url ?? (ctx.slug ? `/${ctx.slug}` : null);
}

function getProgress(item: ListItem): number {
  const ctx = getContext(item);
  const viewed = ctx?.pages_viewed;
  const total = ctx?.total_published_pages;
  if (typeof viewed !== "number" || typeof total !== "number" || total <= 0) {
    return 0;
  }
  if (viewed <= 0) return 0;
  return Math.min(1, viewed / total);
}

function isGoldGated(item: ListItem): boolean {
  const ctx = getContext(item);
  // Backend exposes is_free on comic/release. The list endpoint surfaces it
  // through the index signature. Default to free when the field is absent so
  // the badge errs on the welcoming side.
  return ctx?.is_free === false;
}

export function ReadingListDetailItemRow({
  item,
  isOwner,
  listId,
  isReleaseList,
  dragHandle,
}: ReadingListDetailItemRowProps) {
  const [t] = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ctx = getContext(item);
  const readHref = getReadHref(item);
  const detailsHref = getDetailsHref(item);
  // Comic-context rows point at the comic's own page, which is rendered
  // server-side (Twig) — there's no React Router route for it. We need
  // to do a full-page browser navigation instead of `navigate()` /
  // RouterLink, which otherwise silently no-op (URL changes, no matching
  // route, screen stays blank). Release-context rows point at the reader
  // (`/read/:uuid/...`) which IS a React route, so they keep SPA nav.
  const isComicContext = item.comic != null && item.release == null;
  // Cover resolution.
  //
  // Releases: read `thumbnail_url`. That's the field browse's
  // ReleaseGridCard + the search-v2 picker grid both render, so the
  // detail row stays visually consistent with every other release
  // surface. The backend's `release.image_*_url` field is a separate
  // "release cover avatar" that defaults to a "MISSING COVER IMAGE"
  // placeholder for releases without a custom cover — deliberately
  // not used here.
  //
  // Comics: just walk the standard image_*_url chain on the comic.
  const cover = item.release
    ? item.release.thumbnail_url
    : (ctx?.image_small_url ?? ctx?.image_tiny_url ?? ctx?.image_url);
  // Comics expose `.name`; releases expose `.title` (per the backend
  // hasTitleField trait on ReleaseEntity). Without this fallback the row
  // title was empty on every release-list row.
  const title = ctx?.name ?? ctx?.title ?? "";
  // Curator's per-item note. When present we surface it in the sub line in
  // place of the generic "Comic" / "Release" type label so the editorial
  // voice gets primary placement. For release rows without a curator note,
  // surface the parent comic's name — far more useful than the bare
  // "Release" string.
  const note = item.description?.trim();
  const releaseComicName =
    item.release?.comic?.name ?? item.release?.comic_name;
  const subLabel =
    note ??
    (item.release
      ? (releaseComicName ??
        t("screens.shelf.readingLists.detail.row.releaseSubLabel"))
      : t("screens.shelf.readingLists.detail.row.comicSubLabel"));
  const progress = getProgress(item);
  const goldGated = isGoldGated(item);
  // Entity id used for the remove-from-list mutation. Comics list → comic id;
  // releases list → release id. Falls through to context_entity_id when the
  // typed object isn't there.
  const entityId =
    (isReleaseList ? item.release?.id : item.comic?.id) ??
    item.context_entity_id;
  // Local confirm-dialog state for the trash flow. The dialog renders at
  // the bottom of the row's JSX so it sits outside the row's gradient
  // background container.
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Remove + undo flow lifted from the kebab so both the row's trash
  // button (visible in edit mode) and the kebab's "Remove from list" menu
  // item share the same handler. Single source of truth, single mutation
  // pair, single toast.
  const remove = useMutation({
    mutationFn: () => {
      if (entityId == null) {
        return Promise.reject(new Error("missing entity id"));
      }
      return isReleaseList
        ? postRemoveReleaseFromList(entityId, listId)
        : postRemoveComicFromList(entityId, listId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: READING_LISTS_SHELF_QUERY_KEY,
      });
      queryClient.invalidateQueries({ queryKey: readingListDetailQueryKey(listId) });
    },
    onError: () => {
      toast({
        title: t("screens.shelf.readingLists.detail.row.removeErrorToast"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
  });

  const undoRemove = useMutation({
    mutationFn: () => {
      if (entityId == null) {
        return Promise.reject(new Error("missing entity id"));
      }
      return isReleaseList
        ? postAddReleaseToLists(entityId, { list_ids: [listId] })
        : postAddComicToLists(entityId, { list_ids: [listId] });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: READING_LISTS_SHELF_QUERY_KEY,
      });
      queryClient.invalidateQueries({ queryKey: readingListDetailQueryKey(listId) });
      toast({
        title: t("screens.shelf.readingLists.detail.row.removeUndoneToast", {
          name: title,
        }),
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    },
    onError: () => {
      toast({
        title: t("screens.shelf.readingLists.detail.row.removeUndoErrorToast"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
  });

  const handleRemove = () => {
    if (entityId == null) return;
    setConfirmOpen(true);
  };

  // Fired from the confirm dialog. Closes the dialog, runs the mutation,
  // then shows the undo toast (preserved verbatim from the prior inline
  // flow — same render, same Undo button).
  const handleRemoveConfirmed = () => {
    setConfirmOpen(false);
    remove.mutate();
    toast({
      duration: 5000,
      isClosable: true,
      position: "bottom-left",
      render: ({ onClose: closeToast }) => (
        <Flex
          align="center"
          gap="14px"
          bg="charcoal.charcoal"
          border="1px solid"
          borderColor="blaze.blaze_40"
          borderRadius="10px"
          p="12px 14px"
          boxShadow="0 8px 24px rgba(0, 0, 0, 0.45)"
        >
          <Text
            fontFamily="Roboto"
            fontSize="13px"
            color="dune.dune"
            noOfLines={1}
            maxW="220px"
          >
            {t("screens.shelf.readingLists.detail.row.removedToast", {
              name: title,
            })}
          </Text>
          <Button
            variant="link"
            size="sm"
            color="blaze.blaze"
            fontWeight={700}
            onClick={() => {
              undoRemove.mutate();
              closeToast();
            }}
            isDisabled={undoRemove.isPending}
          >
            {t("screens.shelf.readingLists.detail.row.undoLabel")}
          </Button>
        </Flex>
      ),
    });
  };

  // Owner + entityId gate the destructive paths. When false, the trash
  // button doesn't render and the kebab's Remove entry is hidden.
  const canRemove = isOwner && entityId != null;

  const inner = (
    <Flex
      align="stretch"
      gap="16px"
      h={`${ROW_HEIGHT}px`}
      px="10px"
      py="8px"
      borderRadius="10px"
      bg="rgba(255, 255, 255, 0.02)"
      border="1px solid"
      borderColor="rgba(255, 255, 255, 0.10)"
      transition="background 120ms ease-out, border-color 120ms ease-out"
      _hover={{
        bg: "rgba(255, 255, 255, 0.05)",
        borderColor: "rgba(255, 255, 255, 0.20)",
      }}
      role="group"
    >
      {dragHandle && (
        <Flex align="center" justify="center" flexShrink={0}>
          {dragHandle}
        </Flex>
      )}
      <Box
        position="relative"
        flexShrink={0}
        w={`${COVER_W}px`}
        h={`${COVER_H}px`}
        alignSelf="center"
        borderRadius="6px"
        overflow="hidden"
        bg="transparent.white.05"
      >
        {cover && (
          <ProtectedImage
            src={cover}
            alt=""
            position="absolute"
            inset={0}
            objectFit="cover"
          />
        )}
      </Box>

      <Flex
        direction="column"
        justify="center"
        gap="4px"
        flex="1"
        minW={0}
        position="relative"
      >
        <Text
          fontFamily="Roboto"
          fontSize="16px"
          fontWeight={700}
          lineHeight="1.3"
          color="dune.dune"
          noOfLines={1}
          wordBreak="break-word"
        >
          {title}
        </Text>
        <Text
          fontFamily="Roboto"
          fontSize="13px"
          lineHeight="1.45"
          color="neutral.300"
          noOfLines={1}
        >
          {subLabel}
        </Text>
        {/* Progress track sits flush at the bottom of the title column, per
            Figma — always rendered so rows align identically across the
            list. Empty rail when there's no read history. */}
        <Box
          position="absolute"
          left="0"
          right="0"
          bottom="0"
          h="2px"
          borderRadius="999px"
          bg="transparent.white.10"
          overflow="hidden"
        >
          <Box
            h="100%"
            w={`${progress * 100}%`}
            bg="blaze.blaze"
            borderRadius="999px"
            transition="width 240ms ease-out"
          />
        </Box>
      </Flex>

      {/* Right cluster — status pill + (in edit mode) trash + kebab. The
          old disabled "download" placeholder slot is gone — the platform
          doesn't support digital downloads on web, so it was just dead
          chrome. The trash icon takes its place when editing so the
          destructive action is one click and obviously discoverable. */}
      <Flex align="center" gap="6px" flexShrink={0} pr="4px">
        {/*
          StatusPill (FREE / GOLD) is hidden on mobile when the row is
          in edit mode (canRemove). With ~88px of usable title-column
          width on a 320px screen after the cover + cluster chrome, the
          three-element right cluster (pill + trash + kebab) crowds the
          title to the point of single-character ellipsis — so we drop
          the pill, which is informational rather than actionable.
          Non-edit rows on mobile keep the pill since trash is gone.
        */}
        <Box
          display={
            canRemove ? { base: "none", md: "block" } : undefined
          }
        >
          <StatusPill kind={goldGated ? "gold" : "free"} />
        </Box>
        {canRemove && (
          <IconButton
            paddingLeft="8px"
            aria-label={t(
              "screens.shelf.readingLists.detail.row.removeButtonAria",
              { name: title },
            )}
            icon={<TrashcanIcon w="18px" h="18px" />}
            variant="ghost"
            size="sm"
            color="neutral.white"
            isDisabled={remove.isPending}
            _hover={{ bg: "transparent.white.05" }}
            onClick={(e) => {
              // Block the parent <Link>'s navigation — only edit-mode
              // rows have this button anyway, where the link is already
              // suppressed, but defensive against future call sites.
              e.preventDefault();
              e.stopPropagation();
              handleRemove();
            }}
          />
        )}
        {/*
          Same kebab the series-detail release-card uses (ReleaseCardMenu)
          — gives us "Start/Continue reading", "Release details", and the
          full Share popover via ShareMenuItem. We pick Start vs Continue
          off the row's progress signal (progress > 0 → user has read at
          least one page).
        */}
        <ReleaseCardMenu
          iconSize="24px"
          menuPlacement="bottom-end"
          // Reader URLs are React-routed for both context types (a comic's
          // read_url is its first release's reader URL), so SPA navigate
          // is correct here regardless of isComicContext.
          onStartReading={
            readHref && progress === 0
              ? () => navigate(readHref)
              : undefined
          }
          onContinueReading={
            readHref && progress > 0
              ? () => navigate(readHref)
              : undefined
          }
          // Details URLs differ: comic details are server-rendered (no
          // React route) → full page navigation; release details are an
          // in-app SPA route → router navigate.
          onReleaseDetails={
            detailsHref
              ? () =>
                  isComicContext
                    ? window.location.assign(detailsHref)
                    : navigate(detailsHref)
              : undefined
          }
          detailsLabel={
            isComicContext
              ? t("screens.shelf.readingLists.detail.row.menuComicDetails")
              : undefined
          }
          // Share URL must be absolute. If the backend already shipped an
          // absolute URL (some entities serialise that way) leave it alone;
          // otherwise prepend the current origin.
          shareUrl={
            detailsHref
              ? /^https?:\/\//.test(detailsHref)
                ? detailsHref
                : `${window.location.origin}${detailsHref}`
              : undefined
          }
        />
      </Flex>
    </Flex>
  );

  // Reorderable mode: skip the Link wrap so the row isn't a navigation
  // target while the user is rearranging. Drag listeners on the handle
  // would fight a parent link otherwise (every drag-start would also
  // count as a click).
  //
  // Comic-context rows render a plain anchor (Chakra Link → <a href>)
  // so the comic's server-rendered detail page resolves via a normal
  // browser navigation. Release-context rows wrap in RouterLink so the
  // in-app reader stays SPA.
  let rowOutput: React.ReactNode;
  if (dragHandle || !readHref) {
    rowOutput = <Box w="100%">{inner}</Box>;
  } else if (isComicContext) {
    rowOutput = (
      <Link
        href={readHref}
        _hover={{ textDecoration: "none" }}
        display="block"
        w="100%"
        aria-label={title}
      >
        {inner}
      </Link>
    );
  } else {
    rowOutput = (
      <Link
        as={RouterLink}
        to={readHref}
        _hover={{ textDecoration: "none" }}
        display="block"
        w="100%"
        aria-label={title}
      >
        {inner}
      </Link>
    );
  }

  return (
    <>
      {rowOutput}
      <ConfirmAlertDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleRemoveConfirmed}
        title={t("screens.shelf.readingLists.detail.row.removeConfirmTitle")}
        body={t("screens.shelf.readingLists.detail.row.removeConfirmBody", {
          name: title,
        })}
        confirmLabel={t(
          "screens.shelf.readingLists.detail.row.removeConfirmConfirm",
        )}
        cancelLabel={t(
          "screens.shelf.readingLists.detail.row.removeConfirmCancel",
        )}
        isLoading={remove.isPending}
        isDestructive
      />
    </>
  );
}

// Per Figma design tokens:
//   FREE  — bg: transparent.success.20, border: transparent.success.50,
//           text: success.300, no icon
//   GOLD  — bg: transparent.blaze.20,   border: transparent.blaze.50,
//           text + icon: blaze.300
// `transparent.success.50` and `transparent.blaze.20` aren't in the theme
// transparent palette today — using raw rgba for those two so the
// numeric token values still match the spec exactly.
function StatusPill({ kind }: { kind: "free" | "gold" }) {
  const [t] = useTranslation();
  const isGold = kind === "gold";
  return (
    <Flex
      align="center"
      gap="6px"
      px="8px"
      py="3px"
      borderRadius="4px"
      bg={isGold ? "rgba(255, 90, 44, 0.2)" : "transparent.success.20"}
      border="1px solid"
      borderColor={isGold ? "transparent.blaze.50" : "rgba(0, 171, 101, 0.5)"}
    >
      {isGold && (
        <SealCheckIcon w="12px" h="12px" color="blaze.300" />
      )}
      <Text
        fontFamily="Roboto"
        fontSize="11px"
        fontWeight={700}
        lineHeight="1"
        letterSpacing="0.5px"
        color={isGold ? "blaze.300" : "success.300"}
        textTransform="uppercase"
      >
        {isGold
          ? t("screens.shelf.readingLists.detail.row.gold")
          : t("screens.shelf.readingLists.detail.row.free")}
      </Text>
    </Flex>
  );
}

export const READING_LIST_ITEM_ROW = {
  height: ROW_HEIGHT,
} as const;
